/**
 * Query keys for workspace-related queries and mutations
 * Used with React Query to cache and invalidate queries
 */

export const workspaceKeys = {
  // Base key for all workspace-related queries
  all: ["workspaces"] as const,

  // Key for listing workspaces
  list: (filters?: { page?: number; take?: number; order?: string; search?: string }) =>
    filters
      ? ([...workspaceKeys.all, "list", filters] as const)
      : ([...workspaceKeys.all, "list"] as const),

  // Key for a specific workspace details
  detail: (workspaceId: string | number) => [...workspaceKeys.all, "detail", workspaceId] as const,

  // Key for workspace members
  members: (workspaceId?: string | number) =>
    workspaceId
      ? ([...workspaceKeys.all, "members", workspaceId] as const)
      : ([...workspaceKeys.all, "members"] as const),

  // Key for workspace member details
  member: (workspaceId: string | number, memberId: string | number) =>
    [...workspaceKeys.all, "member", workspaceId, memberId] as const,

  // Note: Separate permission keys removed - permissions now included in main queries

  // Key for workspace invitations
  invitations: (workspaceId?: string | number) =>
    workspaceId
      ? ([...workspaceKeys.all, "invitations", workspaceId] as const)
      : ([...workspaceKeys.all, "invitations"] as const),

  // Key for workspace counts
  counts: () => [...workspaceKeys.all, "counts"] as const,

  // Key for workspace custom roles
  customRoles: (workspaceId?: string | number, includeArchived?: boolean) =>
    workspaceId
      ? ([...workspaceKeys.all, "customRoles", workspaceId, includeArchived ?? false] as const)
      : ([...workspaceKeys.all, "customRoles"] as const),

  // Key for workspace activity logs
  billingSummary: (workspaceId: string | number) =>
    [...workspaceKeys.all, "billingSummary", workspaceId] as const,

  billingInvoices: (workspaceId: string | number) =>
    [...workspaceKeys.all, "billingInvoices", workspaceId] as const,

  dashboardMetrics: (workspaceId: string | number, days?: number) =>
    [...workspaceKeys.all, "dashboardMetrics", workspaceId, days ?? 30] as const,

  aiCreditsSummary: (workspaceId: string | number) =>
    [...workspaceKeys.all, "aiCreditsSummary", workspaceId] as const,

  aiCreditsActivity: (workspaceId: string | number, page?: number, take?: number) =>
    [...workspaceKeys.all, "aiCreditsActivity", workspaceId, page ?? 1, take ?? 20] as const,

  apiKeys: (workspaceId: string | number, view: "mine" | "all" = "mine") =>
    [...workspaceKeys.all, "apiKeys", workspaceId, view] as const,

  claudeConnector: (workspaceId: string | number) =>
    [...workspaceKeys.all, "claudeConnector", workspaceId] as const,

  mcpConnector: (workspaceId: string | number, client: string) =>
    [...workspaceKeys.all, "mcpConnector", workspaceId, client] as const,

  mcpConnections: (workspaceId: string | number, view: "mine" | "all" = "mine") =>
    [...workspaceKeys.all, "mcpConnections", workspaceId, view] as const,

  sso: (workspaceId: string | number) => [...workspaceKeys.all, "sso", workspaceId] as const,
  ssoAffectedAccounts: (workspaceId: string | number) =>
    [...workspaceKeys.all, "sso", workspaceId, "affected-accounts"] as const,

  activityLogs: (
    workspaceId: string | number,
    filters?: {
      page?: number;
      take?: number;
      skip?: number;
      projectId?: string;
      documentId?: string;
      activityType?: string;
      userId?: string;
      startDate?: string;
      endDate?: string;
      days?: number;
      action?: string[];
      scope?: string[];
      performed_by?: string;
    }
  ) =>
    filters
      ? ([...workspaceKeys.all, "activityLogs", workspaceId, filters] as const)
      : ([...workspaceKeys.all, "activityLogs", workspaceId] as const),
};

/**
 * Query keys for project-related queries and mutations
 * Used with React Query to cache and invalidate queries
 */
export const projectKeys = {
  // Base key for all project-related queries
  all: ["projects"] as const,

  // Note: Separate permission keys removed - permissions now included in main queries

  // Key for project details
  detail: (projectId: string | number) => [...projectKeys.all, "detail", projectId] as const,

  // Key for project team
  team: (projectId: string | number) => [...projectKeys.all, "team", projectId] as const,

  // Key for project settings
  settings: (projectId: string | number) => [...projectKeys.all, "settings", projectId] as const,
};
